import { useEffect, useRef, useState } from 'react'
import { CalendarClock } from 'lucide-react'

// 手機滾輪式日期時間選擇：點欄位開啟底部面板，年月日時分各一個可滑動的滾輪
const ROW_H = 40
const VISIBLE_ROWS = 5
const PAD = ROW_H * Math.floor(VISIBLE_ROWS / 2)
const MINUTE_STEP = 5

const pad2 = (n: number) => String(n).padStart(2, '0')

function Wheel({ items, index, onChange }: { items: string[]; index: number; onChange: (i: number) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<number>()

  useEffect(() => {
    const el = ref.current
    if (el && Math.abs(el.scrollTop - index * ROW_H) > 1) el.scrollTop = index * ROW_H
  }, [index, items.length])

  const handleScroll = () => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      const el = ref.current
      if (!el) return
      const i = Math.min(Math.max(Math.round(el.scrollTop / ROW_H), 0), items.length - 1)
      if (i !== index) onChange(i)
    }, 100)
  }

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto snap-y snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{ height: ROW_H * VISIBLE_ROWS, scrollPaddingTop: PAD, paddingTop: PAD, paddingBottom: PAD }}
    >
      {items.map((label, i) => (
        <div
          key={label}
          className={`snap-start flex items-center justify-center cursor-pointer text-base ${i === index ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}
          style={{ height: ROW_H }}
          onClick={() => { onChange(i); ref.current?.scrollTo({ top: i * ROW_H, behavior: 'smooth' }) }}
        >
          {label}
        </div>
      ))}
    </div>
  )
}

const nowTaipei = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Taipei' }))

function initialParts(date: string, time: string) {
  const base = nowTaipei()
  base.setMinutes(Math.ceil((base.getMinutes() + 1) / MINUTE_STEP) * MINUTE_STEP, 0, 0)
  if (date) {
    const [y, m, d] = date.split('-').map(Number)
    base.setFullYear(y, m - 1, d)
  }
  if (time) {
    const [h, mi] = time.split(':').map(Number)
    base.setHours(h, mi - (mi % MINUTE_STEP), 0, 0)
  }
  return { y: base.getFullYear(), m: base.getMonth() + 1, d: base.getDate(), h: base.getHours(), mi: base.getMinutes() }
}

interface Props {
  date: string
  time: string
  onChange: (date: string, time: string) => void
  className?: string
  compact?: boolean
  timeOnly?: boolean
  placeholder?: string
}

export default function WheelDateTimeField({ date, time, onChange, className = '', compact = false, timeOnly = false, placeholder }: Props) {
  const [open, setOpen] = useState(false)
  const [parts, setParts] = useState(() => initialParts(date, time))
  const [err, setErr] = useState('')

  const thisYear = nowTaipei().getFullYear()
  const years = [thisYear, thisYear + 1]
  const daysInMonth = new Date(parts.y, parts.m, 0).getDate()
  const minutes = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP)

  const openSheet = () => { setParts(initialParts(date, time)); setErr(''); setOpen(true) }

  const set = (patch: Partial<typeof parts>) => setParts(prev => {
    const next = { ...prev, ...patch }
    next.d = Math.min(next.d, new Date(next.y, next.m, 0).getDate())
    return next
  })

  const confirm = () => {
    if (timeOnly) { onChange('', `${pad2(parts.h)}:${pad2(parts.mi)}`); setOpen(false); return }
    const picked = new Date(parts.y, parts.m - 1, parts.d, parts.h, parts.mi)
    if (picked < nowTaipei()) { setErr('不可選擇過去的時間'); return }
    onChange(`${parts.y}-${pad2(parts.m)}-${pad2(parts.d)}`, `${pad2(parts.h)}:${pad2(parts.mi)}`)
    setOpen(false)
  }

  const display = timeOnly ? (time || placeholder || '選擇時間') : date && time
    ? (compact ? `${date.slice(5).replace('-', '/')} ${time}` : `${date.replace(/-/g, '/')}　${time}`)
    : (placeholder || (compact ? '日期時間' : '選擇日期與時間'))

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        className={`w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white ${compact ? '!px-2 gap-1' : ''} cursor-pointer text-left ${(timeOnly ? time : date && time) ? 'text-gray-900' : 'text-gray-400'} ${className}`}
      >
        <span>{display}</span>
        {!compact && <CalendarClock size={16} className="text-gray-400" />}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end justify-center" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-xl rounded-t-2xl p-4 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <button className="text-sm text-gray-500 cursor-pointer px-2 py-1" onClick={() => setOpen(false)}>取消</button>
              <span className="text-sm font-semibold text-gray-900">{timeOnly ? '選擇時間' : '選擇日期與時間'}</span>
              <button className="text-sm font-semibold text-[#7f802e] cursor-pointer px-2 py-1" onClick={confirm}>確定</button>
            </div>
            <div className="flex text-xs text-gray-400 text-center">
              {(timeOnly ? ['時', '分'] : ['年', '月', '日', '時', '分']).map(l => <div key={l} className="flex-1">{l}</div>)}
            </div>
            <div className="relative flex">
              <div className="absolute inset-x-0 rounded-lg bg-gray-100/80 pointer-events-none" style={{ top: PAD, height: ROW_H }} />
              <div className="relative flex w-full">
                {!timeOnly && <>
                  <Wheel items={years.map(String)} index={Math.max(years.indexOf(parts.y), 0)} onChange={i => set({ y: years[i] })} />
                  <Wheel items={Array.from({ length: 12 }, (_, i) => pad2(i + 1))} index={parts.m - 1} onChange={i => set({ m: i + 1 })} />
                  <Wheel items={Array.from({ length: daysInMonth }, (_, i) => pad2(i + 1))} index={parts.d - 1} onChange={i => set({ d: i + 1 })} />
                </>}
                <Wheel items={Array.from({ length: 24 }, (_, i) => pad2(i))} index={parts.h} onChange={i => set({ h: i })} />
                <Wheel items={minutes.map(pad2)} index={Math.floor(parts.mi / MINUTE_STEP)} onChange={i => set({ mi: minutes[i] })} />
              </div>
            </div>
            {err && <p className="text-xs text-red-600 text-center">{err}</p>}
          </div>
        </div>
      )}
    </>
  )
}
